import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "react-query";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import { Textarea } from "@/components/ui/Textarea";
import { Combobox, userToComboboxOption } from "@/components/ui/Combobox";
import { Search, ChevronDown, ChevronLeft } from "lucide-react";
import api from "../../lib/api";
import toast from "react-hot-toast";

const CreateReportPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [clients, setClients] = useState([]);
  const [fieldEngineerList, setFieldEngineerList] = useState([]);
  const [peerReviewerList, setPeerReviewerList] = useState([]);
  const [siteReviewerList, setSiteReviewerList] = useState([]);
  const [directorReviewerList, setDirectorReviewerList] = useState([]);
  const [sites, setSites] = useState([]);
  const [filteredSites, setFilteredSites] = useState([]);
  const [showSkeletonField, setShowSkeletonField] = useState(false);
  const [selectedMineType, setSelectedMineType] = useState(null);
  const [isCheckingDuplicate, setIsCheckingDuplicate] = useState(false);

  const [formData, setFormData] = useState({
    client_id: "",
    site_id: "",
    report_name: "",
    field_engineer_id: "",
    peer_reviewer_id: "",
    site_reviewer_id: "",
    director_reviewer_id: "",
    visit_date: "",
    report_stage: "unallocated",
    report_status: "awaiting",
  });

  // Check for duplicate reports on the same site and date
  const checkDuplicateReport = async (siteId, visitDate) => {
    if (!siteId || !visitDate) return false;

    try {
      const response = await api.get(
        `/reports/check-duplicate?site_id=${siteId}&visit_date=${visitDate}`
      );
      return response.data.exists;
    } catch (error) {
      return false;
    }
  };

  // Check for duplicate report names within the same site
  const checkDuplicateReportName = async (reportName, siteId) => {
    if (!reportName || !reportName.trim() || !siteId) return false;

    try {
      const response = await api.get(
        `/reports/check-duplicate-name?report_name=${encodeURIComponent(reportName.trim())}&site_id=${siteId}`
      );
      return response.data.exists;
    } catch (error) {
      return false;
    }
  };

  const reportNameMatchesSiteName = (reportName, siteId) => {
    const selectedSite = sites.find((s) => String(s.id) === String(siteId));
    if (!selectedSite?.name) return false;
    return reportName.trim().toLowerCase() === selectedSite.name.trim().toLowerCase();
  };

  const createMutation = useMutation((reportData) => api.post("/reports", reportData, {}), {
    onSuccess: () => {
      toast.success("Report created successfully");
      queryClient.invalidateQueries(["reports"]);
      queryClient.invalidateQueries(["mine-types"]);
      queryClient.refetchQueries(["mine-types"]);
      navigate("/reports");
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to create report");
    },
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check if all required fields are empty
    const allFieldsEmpty =
      !formData.report_name.trim() &&
      !formData.client_id &&
      !formData.site_id &&
      !formData.field_engineer_id &&
      !formData.peer_reviewer_id &&
      !formData.site_reviewer_id &&
      !formData.director_reviewer_id &&
      !formData.visit_date.trim();

    if (allFieldsEmpty) {
      toast.error("Please fill in the data before adding");
      return;
    }

    // Check individual fields and show specific messages
    let hasErrors = false;

    if (!formData.report_name.trim()) {
      toast.error("Report name is required");
      hasErrors = true;
    }

    if (!formData.client_id) {
      toast.error("Please select Client");
      hasErrors = true;
    }

    if (!formData.site_id) {
      toast.error("Please select Site");
      hasErrors = true;
    }

    if (!formData.visit_date.trim()) {
      toast.error("Visit date is required");
      hasErrors = true;
    }

    if (hasErrors) {
      return;
    }

    if (reportNameMatchesSiteName(formData.report_name, formData.site_id)) {
      toast.error("Report name cannot match the selected site name.");
      return;
    }

    // Check for duplicate report name within the same site before creating
    setIsCheckingDuplicate(true);
    try {
      const isDuplicateName = await checkDuplicateReportName(formData.report_name, formData.site_id);
      if (isDuplicateName) {
        toast.error(
          "A report with this name already exists for this site. Please choose a different report name."
        );
        setIsCheckingDuplicate(false);
        return;
      }
    } catch (error) {
      toast.error("Error checking for duplicate report names. Please try again.");
      setIsCheckingDuplicate(false);
      return;
    }

    // Check for duplicate report before creating
    try {
      const isDuplicate = await checkDuplicateReport(formData.site_id, formData.visit_date);
      if (isDuplicate) {
        toast.error(
          "A report already exists for this site on the selected date. Please choose a different date or site."
        );
        setIsCheckingDuplicate(false);
        return;
      }
    } catch (error) {
      toast.error("Error checking for duplicate reports. Please try again.");
      setIsCheckingDuplicate(false);
      return;
    }
    setIsCheckingDuplicate(false);

    const reportData = {
      client_id: Number(formData.client_id),
      site_id: Number(formData.site_id),
      report_name: formData.report_name.trim(),
      field_engineer_id: formData.field_engineer_id ? Number(formData.field_engineer_id) : null,
      peer_reviewer_id: formData.peer_reviewer_id ? Number(formData.peer_reviewer_id) : null,
      site_reviewer_id: formData.site_reviewer_id ? Number(formData.site_reviewer_id) : null,
      director_reviewer_id: formData.director_reviewer_id ? Number(formData.director_reviewer_id) : null,
      visit_date: formData.visit_date,
      report_stage: formData.report_stage,
      report_status: formData.report_status,
    };

    createMutation.mutate(reportData);
  };

  useEffect(() => {
    const fetchClients = async () => {
      try {
        const clientsRes = await api.get("/clients/all-active-clients");
        let clientList = [];
        if (clientsRes.data?.data && Array.isArray(clientsRes.data.data)) {
          clientList = clientsRes.data.data;
        }
        setClients(clientList);
      } catch (err) {
        toast.error("Failed to load clients");
        setClients([]);
      }
    };

    const fetchSites = async () => {
      try {
        const sitesRes = await api.get("/sites");
        if (sitesRes.data?.data && Array.isArray(sitesRes.data.data)) {
          const siteList = sitesRes.data.data;

          setSites(siteList);
          setFilteredSites(siteList);
        }
      } catch (err) {
        toast.error("Failed to load sites");
        setSites([]);
        setFilteredSites([]);
      }
    };

    const fetchFieldEngineers = async () => {
      try {
        const res = await api.get("/users/all-field-engineer-users");
        let engineerList = [];
        if (res.data?.data && Array.isArray(res.data.data)) {
          engineerList = res.data.data.filter(
            (user) => user.role === "field-engineer" || user.role_name === "Field Engineer"
          );
        }
        setFieldEngineerList(engineerList);
      } catch (err) {
        toast.error("Failed to load field engineers");
        setFieldEngineerList([]);
      }
    };

    const fetchPeerReviewers = async () => {
      try {
        const res = await api.get("/users/all-peer-reviewer-users");
        let reviewerList = [];
        if (res.data?.data && Array.isArray(res.data.data)) {
          reviewerList = res.data.data.filter(
            (user) => user.role === "peer-reviewer" || user.role_name === "Peer Reviewer"
          );
        }
        setPeerReviewerList(reviewerList);
      } catch (err) {
        toast.error("Failed to load peer reviewers");
        setPeerReviewerList([]);
      }
    };

    const fetchSiteReviewers = async () => {
      try {
        const res = await api.get("/users/all-site-reviewer-users");
        let reviewerList = [];
        if (res.data?.data && Array.isArray(res.data.data)) {
          reviewerList = res.data.data.filter(
            (user) => user.role === "site-reviewer" || user.role_name === "Site Reviewer"
          );
        }
        setSiteReviewerList(reviewerList);
      } catch (err) {
        toast.error("Failed to load site reviewers");
        setSiteReviewerList([]);
      }
    };

    const fetchDirectorReviewers = async () => {
      try {
        const res = await api.get("/users/all-director-reviewer-users");
        let reviewerList = [];
        if (res.data?.data && Array.isArray(res.data.data)) {
          reviewerList = res.data.data.filter(
            (user) => user.role === "director-reviewer" || user.role_name === "Director Reviewer"
          );
        }
        setDirectorReviewerList(reviewerList);
      } catch (err) {
        toast.error("Failed to load director reviewers");
        setDirectorReviewerList([]);
      }
    };

    fetchClients();
    fetchSites();
    fetchFieldEngineers();
    fetchPeerReviewers();
    fetchSiteReviewers();
    fetchDirectorReviewers();
  }, []);

  const handleInputChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    if (name === "client") {
      setShowSkeletonField(false);
      setSelectedMineType(null);
      setFormData((prev) => ({ ...prev, client_id: value, site_id: "" }));
      if (value) {
        const clientSites = sites.filter((site) => site.client_id === parseInt(value));
        setFilteredSites(clientSites);
      } else {
        setFilteredSites(sites);
      }
    } else if (name === "site") {
      setFormData((prev) => ({ ...prev, site_id: value }));
      const site = sites.find((s) => String(s.id) === String(value));
      if (site) {
        const hasSkeleton = !!site.has_skeleton && !!site.skeleton_file_path;
        setShowSkeletonField(hasSkeleton);
        if (hasSkeleton) {
          setSelectedMineType({
            id: site.mine_id,
            name: site.mine_type,
            file_path: site.skeleton_file_path,
          });
          toast.success(`Skeleton template available: ${site.mine_type}`);
        } else {
          setSelectedMineType(null);
          toast.info("No skeleton template available for this site");
        }
      } else {
        setShowSkeletonField(false);
        setSelectedMineType(null);
      }
    } else if (name === "fieldEngineer") {
      if (value) {
        setFormData((prev) => ({
          ...prev,
          field_engineer_id: value,
          report_stage: "field_engineer",
          report_status: "awaiting_field_engineer"
        }));
      } else {
        setFormData((prev) => ({
          ...prev,
          field_engineer_id: value,
          report_stage: "unallocated",
          report_status: "awaiting"
        }));
      }
    } else if (name === "peerReviewer") {
      setFormData((prev) => ({ ...prev, peer_reviewer_id: value }));
    } else if (name === "siteReviewer") {
      setFormData((prev) => ({ ...prev, site_reviewer_id: value }));
    } else if (name === "directorReviewer") {
      setFormData((prev) => ({ ...prev, director_reviewer_id: value }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const stageOptions = [
    { value: "unallocated", label: "UNALLOCATED" },
    { value: "field_engineer", label: "FIELD ENGINEER" },
    { value: "peer_reviewer", label: "PEER REVIEWER" },
    { value: "site_reviewer", label: "SITE REVIEWER" },
    { value: "director_reviewer", label: "DIRECTOR REVIEWER" },
    { value: "completed", label: "COMPLETED" },
  ];

  const statusOptions = [
    { value: "awaiting", label: "AWAITING" },
    { value: "awaiting_field_engineer", label: "AWAITING FIELD ENGINEER" },
    { value: "started_field_engineer", label: "STARTED FIELD ENGINEER" },
    { value: "uploaded_field_engineer", label: "UPLOADED FIELD ENGINEER" },
    { value: "failed_peer_review", label: "FAILED PEER REVIEW" },
    { value: "failed_site_review", label: "FAILED SITE REVIEW" },
    { value: "failed_director_review", label: "FAILED DIRECTOR REVIEW" },
    { value: "awaiting_peer_reviewer", label: "AWAITING PEER REVIEWER" },
    { value: "started_peer_review", label: "STARTED PEER REVIEW" },
    { value: "uploaded_peer_review", label: "UPLOADED PEER REVIEW" },
    { value: "awaiting_site_reviewer", label: "AWAITING SITE REVIEWER" },
    { value: "started_site_review", label: "STARTED SITE REVIEW" },
    { value: "uploaded_site_review", label: "UPLOADED SITE REVIEW" },
    { value: "awaiting_director_reviewer", label: "AWAITING DIRECTOR REVIEWER" },
    { value: "started_director_review", label: "STARTED DIRECTOR REVIEW" },
    { value: "uploaded_director_review", label: "UPLOADED DIRECTOR REVIEW" },
    { value: "completed", label: "COMPLETED" },
  ];

  return (
    <div className="">
      <div className="w-full">
        {/* Main Content */}
        <div className="slide-up">
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 gap-4 mb-8 sm:grid-cols-2 lg:grid-cols-4">
              <div className="space-y-3">
                <Label htmlFor="client" className="pl-3 text-xs font-medium uppercase text-gray-0">
                  Client *
                </Label>
                <Combobox
                  options={clients.map(userToComboboxOption)}
                  value={formData.client_id}
                  onValueChange={(value) => handleSelectChange("client", value)}
                  placeholder="SELECT CLIENT"
                  searchPlaceholder="Search client..."
                />
              </div>
              <div className="space-y-3">
                <Label htmlFor="site" className="pl-3 text-xs font-medium uppercase text-gray-0">
                  Site *
                </Label>
                <Combobox
                  options={filteredSites.map((site) => ({
                    value: site.id,
                    label: site.name,
                  }))}
                  value={formData.site_id}
                  onValueChange={(value) => handleSelectChange("site", value)}
                  placeholder="SELECT SITE"
                  searchPlaceholder="Search site..."
                  disabled={!formData.client_id}
                />
              </div>
              <div className="space-y-3">
                <Label htmlFor="name" className="pl-3 text-xs font-medium uppercase text-gray-0">
                  Report Name *
                </Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  placeholder="Report Name"
                  value={formData.report_name}
                  onChange={(e) => handleInputChange("report_name", e.target.value)}
                />
              </div>
            </div>
            <div className="grid grid-cols-1 gap-4 mb-8 sm:grid-cols-2 lg:grid-cols-4">
              <div className="space-y-3">
                <Label
                  htmlFor="fieldEngineer"
                  className="pl-3 text-xs font-medium uppercase text-gray-0"
                >
                  Field Engineer
                </Label>
                <Combobox
                  options={fieldEngineerList.map(userToComboboxOption)}
                  value={formData.field_engineer_id}
                  onValueChange={(value) => handleSelectChange("fieldEngineer", value)}
                  placeholder="SELECT FIELD ENGINEER"
                  searchPlaceholder="Search field engineer..."
                />
              </div>
              <div className="space-y-3">
                <Label
                  htmlFor="peerReviewer"
                  className="pl-3 text-xs font-medium uppercase text-gray-0"
                >
                  Peer Reviewer
                </Label>
                <Combobox
                  options={peerReviewerList.map(userToComboboxOption)}
                  value={formData.peer_reviewer_id}
                  onValueChange={(value) => handleSelectChange("peerReviewer", value)}
                  placeholder="SELECT PEER REVIEWER"
                  searchPlaceholder="Search peer reviewer..."
                />
              </div>
              <div className="space-y-3">
                <Label
                  htmlFor="siteReviewer"
                  className="pl-3 text-xs font-medium uppercase text-gray-0"
                >
                  Site Reviewer
                </Label>
                <Combobox
                  options={siteReviewerList.map(userToComboboxOption)}
                  value={formData.site_reviewer_id}
                  onValueChange={(value) => handleSelectChange("siteReviewer", value)}
                  placeholder="SELECT SITE REVIEWER"
                  searchPlaceholder="Search site reviewer..."
                />
              </div>
              <div className="space-y-3">
                <Label
                  htmlFor="directorReviewer"
                  className="pl-3 text-xs font-medium uppercase text-gray-0"
                >
                  Director Reviewer
                </Label>
                <Combobox
                  options={directorReviewerList.map(userToComboboxOption)}
                  value={formData.director_reviewer_id}
                  onValueChange={(value) => handleSelectChange("directorReviewer", value)}
                  placeholder="SELECT DIRECTOR REVIEWER"
                  searchPlaceholder="Search director reviewer..."
                />
              </div>
            </div>
            <div className="grid grid-cols-1 gap-4 mb-8 sm:grid-cols-2 lg:grid-cols-4">
              <div className="space-y-3">
                <Label
                  htmlFor="visit_date"
                  className="pl-3 text-xs font-medium uppercase text-gray-0"
                >
                  Visit Date *
                </Label>
                <Input
                  id="visit_date"
                  name="visit_date"
                  type="date"
                  value={formData.visit_date}
                  onChange={(e) => handleInputChange("visit_date", e.target.value)}
                />
              </div>
              <div className="space-y-3">
                <Label
                  htmlFor="reportStage"
                  className="pl-3 text-xs font-medium uppercase text-gray-0"
                >
                  Report Stage:
                </Label>
                <div className="flex items-center justify-between w-full p-4 text-sm font-normal rounded-t-lg rounded-b-none cursor-not-allowed h-52 bg-navy-100 text-gray-0 opacity-60">
                  {formData.report_stage === "field_engineer" ? "Field Engineer" : "Unallocated"}
                </div>
              </div>
              <div className="space-y-3">
                <Label
                  htmlFor="reportStatus"
                  className="pl-3 text-xs font-medium uppercase text-gray-0"
                >
                  Report Status:
                </Label>
                <div className="flex items-center justify-between w-full p-4 text-sm font-normal rounded-t-lg rounded-b-none cursor-not-allowed h-52 bg-navy-100 text-gray-0 opacity-60">
                  {formData.report_status === "awaiting_field_engineer" ? "Awaiting Field Engineer" : "Awaiting"}
                </div>
              </div>
            </div>
            {showSkeletonField && (
              <div className="slide-up mb-8 grid grid-cols-[1fr] gap-4">
                <div className="flex flex-col gap-2 space-y-3">
                  <p className="m-0 text-gray-0">
                    Skeleton reports name :{" "}
                    {selectedMineType?.file_path
                      ? selectedMineType.file_path
                        .split("/")
                        .pop()
                        .replace(/\.[^/.]+$/, "")
                      : ""}
                  </p>
                  <p className="!m-0 text-gray-0">Mine type : {selectedMineType?.name}</p>
                </div>
              </div>
            )}

            {/* Submit Buttons */}
            <div className="flex justify-end border-t border-b border-gray-0">
              <div className="flex items-center justify-center w-auto">
                <Link className="border-r rounded-b-none border-gray-0" to="/reports">
                  <Button
                    variant="secondary"
                    className="flex items-center justify-center h-auto px-10 py-5 text-xs font-normal uppercase rounded-none text-gray-0 hover:scale-100"
                  >
                    <ChevronLeft className="w-4 h-4 mr-2 text-red-500" />
                    Back
                  </Button>
                </Link>
                <Button
                  className="flex items-center justify-center h-auto px-10 py-5 text-xs font-normal text-red-500 uppercase bg-transparent border-0 rounded-none rounded-b-none hover:scale-100"
                  type="submit"
                  disabled={createMutation.isLoading || isCheckingDuplicate}
                >
                  {isCheckingDuplicate
                    ? "Checking..."
                    : createMutation.isLoading
                      ? "Saving..."
                      : "Save"}
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateReportPage;
